import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { selectUserInfo } from 'redux/userInfo/userInfoSlice';
import getCases from 'api/cases/getCases';
import getContactList from 'api/contacts/getContactList';
import getCustomers from 'api/customer/getCustomers';
import getCanTabApi from 'api/cantabs/getCanTabApi';
import semanticSearch from 'api/search/semanticSearch';
import filterCanTabs from 'containers/cantabs/libs/filterCanTabs';
import { getDashboardData } from 'api/cantrack/getDashboardData';
import { dashboardKeys } from 'containers/pages/cantrackDashboard/hooks/dashboardQueryKeys';
import { parseMode } from 'components/commandPalette/types';
import { buildActionsFromMenus, filterActions } from 'components/commandPalette/actions';
import { useSearchContext } from './useSearchContext';
import type { SearchResult, SearchMode, ResultCategory } from 'components/commandPalette/types';
import type { DashboardData, PinnedCase, TodayEntry, MyCaseSummary } from 'containers/pages/cantrackDashboard/types';

const MAX_RESULTS_PER_CATEGORY = 5;

interface UseCommandPaletteSearchReturn {
  query: string;
  setQuery: (q: string) => void;
  mode: SearchMode;
  results: SearchResult[];
  isLoading: boolean;
  flatResults: SearchResult[];
}

// --- Helpers to build contextual results from dashboard data ---

function pinnedCasesToResults(
  pinnedCases: PinnedCase[],
  navigate: (path: string) => void,
): SearchResult[] {
  return pinnedCases.map((c) => ({
    id: `pinned-${c.Id}`,
    category: 'pinned' as ResultCategory,
    title: `#${c.Id} ${c.Title}`,
    subtitle: [c.CustomerName, c.StatusTitle].filter(Boolean).join(' - '),
    badge: 'Pinned',
    onSelect: () => navigate(`/cantrack/cases/${c.Id}`),
  }));
}

function todayEntriesToResults(
  entries: TodayEntry[],
  navigate: (path: string) => void,
): SearchResult[] {
  // Deduplicate by caseId, aggregate hours
  const byCaseId = new Map<number, { entry: TodayEntry; totalHours: number }>();
  for (const entry of entries) {
    const existing = byCaseId.get(entry.caseId);
    const hours = (entry.timeRegular || 0) + (entry.timeOver || 0);
    if (existing) {
      existing.totalHours += hours;
    } else {
      byCaseId.set(entry.caseId, { entry, totalHours: hours });
    }
  }

  return Array.from(byCaseId.values()).map(({ entry, totalHours }) => ({
    id: `today-${entry.caseId}`,
    category: 'activeToday' as ResultCategory,
    title: `#${entry.caseId} ${entry.caseTitle}`,
    subtitle: `${totalHours.toFixed(1)}h logged today`,
    badge: 'Today',
    onSelect: () => navigate(`/cantrack/cases/${entry.caseId}`),
  }));
}

function myCasesToResults(
  cases: MyCaseSummary[],
  query: string,
  navigate: (path: string) => void,
): SearchResult[] {
  let filtered = cases;
  if (query) {
    const lower = query.toLowerCase();
    filtered = cases.filter(
      (c) =>
        c.Title.toLowerCase().includes(lower) ||
        c.CustomerName.toLowerCase().includes(lower) ||
        String(c.Id).includes(lower),
    );
  }
  return filtered.slice(0, MAX_RESULTS_PER_CATEGORY).map((c) => ({
    id: `mycase-${c.Id}`,
    category: 'myCases' as ResultCategory,
    title: `#${c.Id} ${c.Title}`,
    subtitle: [c.CustomerName, c.StatusTitle, c.TimeSpent !== '0' ? `${c.TimeSpent}h` : ''].filter(Boolean).join(' - '),
    badge: 'My Case',
    onSelect: () => navigate(`/cantrack/cases/${c.Id}`),
  }));
}

function recentActivityToResults(
  activity: DashboardData['recentActivity'],
  navigate: (path: string) => void,
): SearchResult[] {
  // Deduplicate by caseId, show most recent
  const seen = new Set<number>();
  const unique = activity.filter((a) => {
    if (seen.has(a.caseId)) return false;
    seen.add(a.caseId);
    return true;
  });

  return unique.slice(0, MAX_RESULTS_PER_CATEGORY).map((a) => ({
    id: `recent-${a.id}`,
    category: 'recentActivity' as ResultCategory,
    title: `#${a.caseId} ${a.caseTitle}`,
    subtitle: [a.title, a.authorName].filter(Boolean).join(' - '),
    onSelect: () => navigate(`/cantrack/cases/${a.caseId}`),
  }));
}

export function useCommandPaletteSearch(isOpen: boolean): UseCommandPaletteSearchReturn {
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const { priorityOrder } = useSearchContext();
  const { userInfo } = useSelector(selectUserInfo) as any;

  const userId = userInfo?.userId ?? '';
  const employeeId = userInfo?.employeeId ?? '';
  const contactId = userInfo?.contactId ?? '';

  const { mode, cleanQuery } = useMemo(() => parseMode(query), [query]);

  const handleSetQuery = useCallback((q: string) => {
    setQuery(q);
  }, []);

  // --- Dashboard data (pinned cases, my cases, today's entries, recent activity) ---
  // Piggyback on the same cache key the dashboard uses, so if the user
  // already visited the dashboard we get this data for free.
  const { data: dashboardData } = useQuery<DashboardData>({
    queryKey: dashboardKeys.data(userId),
    queryFn: () => getDashboardData(userId, contactId, employeeId),
    staleTime: 30_000,
    enabled: !!userId,
  });

  // --- Sidebar menus (RBAC-filtered, cached) ---
  const { data: sidebarMenus } = useQuery({
    queryKey: ['commandPalette', 'sidebarMenus'],
    queryFn: async () => {
      const raw = await getCanTabApi();
      return filterCanTabs(raw);
    },
    staleTime: 5 * 60_000,
  });

  const navigationActions = useMemo(
    () => (sidebarMenus ? buildActionsFromMenus(sidebarMenus) : []),
    [sidebarMenus],
  );

  // --- Contextual quick-access (empty query state) ---
  const pinnedResults = useMemo(
    () => pinnedCasesToResults(dashboardData?.pinnedCases ?? [], navigate),
    [dashboardData?.pinnedCases, navigate],
  );

  const todayResults = useMemo(
    () => todayEntriesToResults(dashboardData?.todayEntries ?? [], navigate),
    [dashboardData?.todayEntries, navigate],
  );

  const recentResults = useMemo(
    () => recentActivityToResults(dashboardData?.recentActivity ?? [], navigate),
    [dashboardData?.recentActivity, navigate],
  );

  // --- Actions (client-side, instant) ---
  const actionResults = useMemo(() => {
    if (mode !== 'actions' && mode !== 'general') return [];
    return filterActions(navigationActions, mode === 'actions' ? cleanQuery : cleanQuery, navigate)
      .slice(0, MAX_RESULTS_PER_CATEGORY);
  }, [mode, cleanQuery, navigate, navigationActions]);

  // --- My Cases (client-side filter over cached dashboard data) ---
  const myCaseResults = useMemo(() => {
    if (mode !== 'myCases' && mode !== 'general') return [];
    return myCasesToResults(dashboardData?.myCases ?? [], cleanQuery, navigate);
  }, [mode, cleanQuery, dashboardData?.myCases, navigate]);

  // --- Cases (API search) ---
  const shouldSearchCases = isOpen && cleanQuery.length >= 2 && (mode === 'general' || mode === 'caseId');
  const caseQueryPayload = useMemo(() => {
    if (mode === 'caseId' && cleanQuery) {
      return { Id: parseInt(cleanQuery, 10), Take: MAX_RESULTS_PER_CATEGORY };
    }
    return { SearchKeyword: cleanQuery, Take: MAX_RESULTS_PER_CATEGORY, Page: 1 };
  }, [mode, cleanQuery]);

  const {
    data: casesData,
    isLoading: casesLoading,
  } = useQuery({
    queryKey: ['commandPalette', 'cases', caseQueryPayload],
    queryFn: () => getCases(caseQueryPayload),
    enabled: shouldSearchCases,
    staleTime: 30_000,
  });

  // Build case results, boosting pinned/my cases with badges
  const pinnedIds = useMemo(
    () => new Set((dashboardData?.pinnedCases ?? []).map((c) => c.Id)),
    [dashboardData?.pinnedCases],
  );
  const myCaseIds = useMemo(
    () => new Set((dashboardData?.myCases ?? []).map((c) => c.Id)),
    [dashboardData?.myCases],
  );
  const todayCaseIds = useMemo(() => {
    const ids = new Set<number>();
    for (const e of dashboardData?.todayEntries ?? []) ids.add(e.caseId);
    return ids;
  }, [dashboardData?.todayEntries]);

  const caseResults: SearchResult[] = useMemo(() => {
    if (!casesData?.Data) return [];
    return casesData.Data.slice(0, MAX_RESULTS_PER_CATEGORY).map((c: any) => {
      const badge = pinnedIds.has(c.Id)
        ? 'Pinned'
        : todayCaseIds.has(c.Id)
          ? 'Today'
          : myCaseIds.has(c.Id)
            ? 'My Case'
            : undefined;

      return {
        id: `case-${c.Id}`,
        category: 'cases' as ResultCategory,
        title: `#${c.Id} ${c.Title}`,
        subtitle: [c.CustomerName, c.StatusTitle].filter(Boolean).join(' - '),
        badge,
        onSelect: () => navigate(`/cantrack/cases/${c.Id}`),
      };
    });
  }, [casesData, navigate, pinnedIds, myCaseIds, todayCaseIds]);

  // --- Contacts ---
  const shouldSearchContacts = isOpen && cleanQuery.length >= 2 && (mode === 'general' || mode === 'contacts');

  const {
    data: contactsData,
    isLoading: contactsLoading,
  } = useQuery({
    queryKey: ['commandPalette', 'contacts', cleanQuery],
    queryFn: () => getContactList(),
    enabled: shouldSearchContacts,
    staleTime: 30_000,
  });

  const contactResults: SearchResult[] = useMemo(() => {
    if (!contactsData) return [];
    const contacts = Array.isArray(contactsData) ? contactsData : contactsData.Data || [];
    const lower = cleanQuery.toLowerCase();
    return contacts
      .filter((c: any) => {
        const name = `${c.FirstName || ''} ${c.LastName || ''}`.toLowerCase();
        const email = (c.Email || c.EmailWork || '').toLowerCase();
        return name.includes(lower) || email.includes(lower);
      })
      .slice(0, MAX_RESULTS_PER_CATEGORY)
      .map((c: any) => ({
        id: `contact-${c.Id || c.ContactId}`,
        category: 'contacts' as ResultCategory,
        title: `${c.FirstName || ''} ${c.LastName || ''}`.trim(),
        subtitle: c.Email || c.EmailWork || c.CompanyName || '',
        onSelect: () => navigate('/contacts'),
      }));
  }, [contactsData, cleanQuery, navigate]);

  // --- Customers ---
  const shouldSearchCustomers = isOpen && cleanQuery.length >= 2 && mode === 'general';

  const {
    data: customersData,
    isLoading: customersLoading,
  } = useQuery({
    queryKey: ['commandPalette', 'customers', cleanQuery],
    queryFn: () => getCustomers({ SearchKeyword: cleanQuery, Take: MAX_RESULTS_PER_CATEGORY, Page: 1 }),
    enabled: shouldSearchCustomers,
    staleTime: 30_000,
  });

  const customerResults: SearchResult[] = useMemo(() => {
    if (!customersData?.Data) return [];
    return customersData.Data.slice(0, MAX_RESULTS_PER_CATEGORY).map((c: any) => ({
      id: `customer-${c.Id}`,
      category: 'customers' as ResultCategory,
      title: c.Name || c.CompanyName || '',
      subtitle: c.City ? `${c.City}${c.Province ? `, ${c.Province}` : ''}` : '',
      onSelect: () => navigate('/customers'),
    }));
  }, [customersData, navigate]);

  // --- Semantic Search (Pinecone, parallel with keyword search) ---
  const shouldSearchSemantic = isOpen && cleanQuery.length >= 3 && mode === 'general';

  const {
    data: semanticData,
    isLoading: semanticLoading,
  } = useQuery({
    queryKey: ['commandPalette', 'semantic', cleanQuery],
    queryFn: async () => {
      const response = await semanticSearch(cleanQuery, MAX_RESULTS_PER_CATEGORY);
      return response?.data?.results ?? [];
    },
    enabled: shouldSearchSemantic,
    staleTime: 30_000,
    retry: false, // Don't retry if Pinecone isn't configured
  });

  const semanticResults: SearchResult[] = useMemo(() => {
    if (!semanticData || !Array.isArray(semanticData) || semanticData.length === 0) return [];
    return semanticData
      .filter((r: any) => r?.metadata?.caseId)
      .map((r: any) => {
        const score = typeof r.score === 'number' ? Math.round(r.score * 100) : 0;
        return {
          id: `semantic-${r.metadata.caseId}`,
          category: 'cases' as ResultCategory,
          title: `#${r.metadata.caseId} ${r.metadata.title || ''}`,
          subtitle: [r.metadata.customerName, r.metadata.statusTitle, `${score}% match`].filter(Boolean).join(' - '),
          badge: 'AI Match',
          onSelect: () => navigate(`/cantrack/cases/${r.metadata.caseId}`),
        };
      });
  }, [semanticData, navigate]);

  // --- Merge & sort ---
  const isLoading = casesLoading || contactsLoading || customersLoading || semanticLoading;

  const flatResults: SearchResult[] = useMemo(() => {
    // Empty query → show contextual quick-access
    if (!query) {
      const results: SearchResult[] = [];
      if (pinnedResults.length > 0) results.push(...pinnedResults);
      if (todayResults.length > 0) results.push(...todayResults);
      if (recentResults.length > 0) results.push(...recentResults);
      return results;
    }

    // Prefix modes
    if (mode === 'actions') return actionResults;
    if (mode === 'caseId') return caseResults;
    if (mode === 'contacts') return contactResults;
    if (mode === 'myCases') return myCaseResults;

    // General mode: context-priority ordering
    const allByCategory: Record<string, SearchResult[]> = {
      cases: caseResults,
      contacts: contactResults,
      customers: customerResults,
      navigation: actionResults,
    };

    const sorted: SearchResult[] = [];
    const lower = cleanQuery.toLowerCase();

    // Strong navigation matches go first — if someone types "timesheets"
    // they obviously want the nav action, not case search results
    if (cleanQuery.length >= 2) {
      const strongNavMatches = actionResults.filter((r) => {
        const title = r.title.replace('Go to ', '').toLowerCase();
        return title.startsWith(lower) || title === lower;
      });
      const weakNavMatches = actionResults.filter((r) => {
        const title = r.title.replace('Go to ', '').toLowerCase();
        return !title.startsWith(lower) && r.title.toLowerCase().includes(lower);
      });
      sorted.push(...strongNavMatches, ...weakNavMatches);
    }

    // Matching pinned cases next
    if (cleanQuery.length >= 2) {
      const matchingPinned = pinnedResults.filter(
        (r) => r.title.toLowerCase().includes(lower),
      );
      if (matchingPinned.length > 0) sorted.push(...matchingPinned);
    }

    // Then context-priority results, skipping already-added items
    for (const cat of priorityOrder) {
      if (cat === 'navigation') continue; // Already handled above
      const categoryResults = allByCategory[cat] || [];
      const existingIds = new Set(sorted.map((r) => r.id));
      const deduped = categoryResults.filter((r) => {
        const caseId = r.id.replace('case-', '');
        return !existingIds.has(`pinned-${caseId}`) && !existingIds.has(r.id);
      });
      sorted.push(...deduped);
    }

    // Append semantic results, deduplicating against keyword results
    if (semanticResults.length > 0) {
      const existingCaseIds = new Set(
        sorted
          .filter((r) => r.category === 'cases' || r.category === 'pinned')
          .map((r) => {
            const match = r.id.match(/(\d+)$/);
            return match ? match[1] : '';
          })
          .filter(Boolean),
      );

      for (const sr of semanticResults) {
        const caseId = sr.id.replace('semantic-', '');
        if (existingCaseIds.has(caseId)) {
          // Case already in results — add AI Match badge to the existing result
          const existing = sorted.find(
            (r) => r.id === `case-${caseId}` || r.id === `pinned-${caseId}`,
          );
          if (existing && !existing.badge) {
            existing.badge = 'AI Match';
          }
        } else {
          sorted.push(sr);
        }
      }
    }

    return sorted;
  }, [
    query, mode, cleanQuery, priorityOrder,
    pinnedResults, todayResults, recentResults,
    caseResults, contactResults, customerResults,
    actionResults, myCaseResults, semanticResults,
  ]);

  return {
    query,
    setQuery: handleSetQuery,
    mode,
    results: flatResults,
    isLoading: isLoading && cleanQuery.length >= 2,
    flatResults,
  };
}
